import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialSkills } from "../data/initialData";

const skillsByCategoryOrder = [
  "AI & Machine Learning",
  "Backend Engineering",
  "Frontend Engineering",
  "DevOps & Infrastructure",
  "Databases",
  "Tools"
];

const categoryIcons = {
  "AI & Machine Learning": "🧠",
  "Backend Engineering": "⚙️",
  "Frontend Engineering": "🎨",
  "DevOps & Infrastructure": "🔧",
  "Databases": "🗄️",
  "Tools": "🔨"
};

const Skills = () => {
  const [skills] = useLocalStorage('skills', initialSkills);

  // Map legacy category names to new ones
  const categoryMap = {
    "Machine Learning": "AI & Machine Learning",
    "Natural Language Processing": "AI & Machine Learning",
    "Frontend": "Frontend Engineering",
    "Backend": "Backend Engineering",
    "DevOps": "DevOps & Infrastructure",
    "Database": "Databases",
    "Cloud": "DevOps & Infrastructure",
  };

  const grouped = skillsByCategoryOrder.map(cat => ({
    category: cat,
    icon: categoryIcons[cat] || "📦",
    items: skills.filter(s => {
      const mapped = categoryMap[s.category] || s.category;
      return mapped === cat;
    })
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1, y: 0,
      transition: { staggerChildren: 0.03 }
    }
  };

  const skillVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1, scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  return (
    <motion.section
      id="skills"
      className="py-24 relative overflow-hidden bg-slate-950"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <motion.div
        className="absolute bottom-20 left-20 w-80 h-80 bg-blue-500/5 rounded-full blur-[120px]"
        animate={{ x: [0, 60, 0], y: [0, -30, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="section-container relative z-10">
        <motion.div className="text-center mb-16" variants={sectionVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
          >
            Capabilities
          </motion.span>
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Technical <span className="gradient-text">Expertise</span>
          </motion.h2>
          <motion.p className="text-slate-400 max-w-2xl mx-auto text-base">
            Professional capability groups spanning AI, infrastructure, and engineering
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {grouped.map((group) => (
            <motion.div
              key={group.category}
              variants={sectionVariants}
              className="glass-card rounded-2xl p-6 hover:border-cyan-500/20 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xl">{group.icon}</span>
                <h3 className="text-base font-semibold text-white font-mono tracking-wide">
                  {group.category}
                </h3>
                <span className="ml-auto text-xs text-slate-500 font-mono">
                  {group.items.length}
                </span>
              </div>
              <motion.div
                className="flex flex-wrap gap-2"
                variants={containerVariants}
              >
                {group.items.map((skill) => (
                  <motion.span
                    key={skill.id}
                    variants={skillVariants}
                    className="px-3 py-1.5 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-300 hover:border-cyan-500/30 hover:text-cyan-300 transition-all cursor-default"
                    whileHover={{ scale: 1.05, y: -1 }}
                  >
                    {skill.name}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default Skills;
